<template>
  <v-dialog v-model="dialog" width="900" :persistent="true">
    <v-card>
      <v-toolbar card dark color="primary">
        <v-toolbar-title>{{ modalTitle }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-toolbar-items>
          <v-btn icon dark small @click="close">
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar-items>
      </v-toolbar>
      <v-card-text>
        <v-container grid-list-md>
          <v-form ref="form" v-model="valid" lazy-validation>
            <v-layout wrap align-center>
              <v-flex xs6>
                <v-text-field
                    :label="'app.name' | trans"
                    required
                    v-model="food.name"
                    :rules="rules.name"
                ></v-text-field>
              </v-flex>
              <v-flex xs2>
                <v-text-field
                    :label="`app.foods.portions_count` | trans"
                    v-model="food.portions"
                    full
                    :rules="rules.portions"
                    type="number"
                ></v-text-field>
              </v-flex>
            </v-layout>
            <v-layout row wrap v-if="hasPermission('food_system_create')">
              <v-flex xs12 class="system-food-checkbox-container">
                <v-checkbox class="system-food-checkbox" color="primary" v-model="food.is_system"
                            :label="'app.foods.system' | trans"></v-checkbox>
              </v-flex>
            </v-layout>
            <v-layout align-center justify-center>
              <v-flex xs12>
                <v-autocomplete
                    class="product-search-input"
                    v-model="productSearchSelected"
                    :items="productsSearchResults"
                    :loading="productsSearchLoading"
                    :search-input.sync="productsSearchQuery"
                    label="Szukaj produktu"
                    item-text="name"
                    item-value="id"
                    clearable
                    :return-object="true"
                    @input="onpProductSearchSelected"
                    dense
                    solo
                    hide-details
                    @focusin="productsSearchFocused = true"
                    @focusout="productsSearchFocused = false"
                >
                  <template v-slot:append>
                    <v-icon :color="productsSearchFocused ? `success` : ``">search</v-icon>
                  </template>
                  <template v-slot:item="{item}">
                    <template>
                      {{ item.name }}
                      <template v-if="item.shared">
                        <v-tooltip right>
                          <template #activator="{ on }">
                            <v-icon v-on="on" small color="success">share</v-icon>
                          </template>
                          <span>{{ trans('app.sharing.shared_by', {name: item.owned_by}) }}</span>
                        </v-tooltip>
                      </template>
                      <div class="product-search-nutrients">
                        <span v-if="productHasNutrient(item, KCAL())">
                          Kcal: {{ item.nutrients.find((nutrient) => nutrient.id === KCAL())?.value|round(0) }}
                        </span>
                        <span v-if="productHasNutrient(item,CARBO())">
                          W: {{ item.nutrients.find((nutrient) => nutrient.id === CARBO())?.value|round(0) }}<small>g</small>
                        </span>
                        <span v-if="productHasNutrient(item,PROTEIN())">
                          B: {{ item.nutrients.find((nutrient) => nutrient.id === PROTEIN())?.value|round(0) }}<small>g</small>
                        </span>
                        <span v-if="productHasNutrient(item,FATS())">
                          T: {{ item.nutrients.find((nutrient) => nutrient.id === FATS())?.value|round(0) }}<small>g</small>
                        </span>
                      </div>
                    </template>
                  </template>
                  <template v-slot:append-item>
                    <div class="text-center">
                      <v-btn color="success" v-if="hasMoreProductsSearchResults" :disabled="productsSearchLoading"
                             :loading="productsSearchLoading" @click="loadMoreSearchResults()">
                        {{ `app.foods.load_more` | trans }}
                      </v-btn>
                    </div>
                  </template>
                </v-autocomplete>
              </v-flex>
            </v-layout>
            <v-layout align-center>
              <v-flex xs12>
                <div>
                  <div class="v-table__overflow">
                    <table class="v-datatable v-table theme--light">
                      <thead>
                      <tr>
                        <th v-for="header in headers" :key="header.text" class="column text-xs-left">
                          <span class="header-cell">{{ header.text }}</span>
                        </th>
                      </tr>
                      </thead>
                      <draggable v-model="food.products"
                                 handle=".drag-handle"
                                 tag="tbody"
                      >
                        <tr v-for="(item, index) in food.products" :key="item._rowId">
                          <td>
                            <v-icon class="drag-handle" small>swap_vert</v-icon>
                          </td>
                          <td>
                            {{ item.name }}
                          </td>
                          <td>
                            <v-text-field class="product-amount-input"
                                          @input="productAmountInput(item, $event)"
                                          @change="productAmountCommit(item, $event)"
                                          @blur="productAmountCommit(item, item.amount)"
                                          type="number"
                                          step="1"
                                          inputmode="decimal"
                                          v-model.number="item.amount"
                                          hide-details
                            ></v-text-field>
                          </td>
                          <td>
                            <v-select class="weight-select" @change="recalculateProductNutrients(item)"
                                      v-model="item.weight" item-text="name"
                                      hide-details :return-object="true" :items="getProductWeights(item)"></v-select>
                          </td>
                          <td>
                            {{ item.kcal | round(2) }}
                          </td>
                          <td>
                            {{ item.carbo | round(2) }}
                          </td>
                          <td>
                            {{ item.protein | round(2) }}
                          </td>
                          <td>
                            {{ item.fat | round(2) }}
                          </td>
                          <td>
                            {{ item.fiber | round(2) }}
                          </td>
                          <td>
                            <icon-button
                                icon="delete"
                                :color="'red'"
                                cls="mr-0 ml-0"
                                @click="removeProduct(index)"
                                :tooltip="{top: true, bottom: false, attach: false}"
                                :iconOptions="{small: true}"
                            >
                              {{ 'app.delete' | trans }}
                            </icon-button>
                          </td>
                        </tr>
                      </draggable>
                      <tfoot>
                      <tr>
                        <td colspan="10"/>
                      </tr>
                      <tr v-if="food.portions > 1">
                        <td colspan="2" class="total-n-portions">
                          <strong>
                            {{ trans('app.foods.total_n_portions', {portions: food.portions}) }}
                          </strong>
                        </td>
                        <td></td>
                        <td class="text-center">
                          <strong>
                            {{ totalFoodWeight | round(2) }} g
                          </strong>
                        </td>
                        <td>
                          <strong>
                            {{ totalFoodKcal | round(2) }}
                          </strong>
                        </td>
                        <td>
                          <strong>
                            {{ totalFoodCarbo | round(2) }}
                          </strong>
                        </td>
                        <td>
                          <strong>
                            {{ totalFoodProtein | round(2) }}
                          </strong>
                        </td>
                        <td>
                          <strong>
                            {{ totalFoodFat | round(2) }}
                          </strong>
                        </td>
                        <td>
                          <strong>
                            {{ totalFoodFiber | round(2) }}
                          </strong>
                        </td>
                        <td></td>
                      </tr>
                      <tr>
                        <td colspan="2" class="total-n-portions">
                          <strong>
                            {{ trans('app.foods.total_n_portions', {portions: 1}) }}
                          </strong>
                        </td>
                        <td></td>
                        <td class="text-center">
                          <strong>
                            {{ totalPortionWeight | round(2) }} g
                          </strong>
                        </td>
                        <td>
                          <strong>
                            {{ totalPortionKcal | round(2) }}
                          </strong>
                        </td>
                        <td>
                          <strong>
                            {{ totalPortionCarbo | round(2) }}
                          </strong>
                        </td>
                        <td>
                          <strong>
                            {{ totalPortionProtein | round(2) }}
                          </strong>
                        </td>
                        <td>
                          <strong>
                            {{ totalPortionFat | round(2) }}
                          </strong>
                        </td>
                        <td>
                          <strong>
                            {{ totalPortionFiber | round(2) }}
                          </strong>
                        </td>
                        <td></td>
                      </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
                <div class="additional-info-container">
                  <v-layout align-center class="pb-3">
                    <v-flex xs3>
                      <strong>{{ `app.foods.additional_information`| trans }}:</strong>
                      <v-tooltip top>
                        <template v-slot:activator="{ on }">
                          <v-icon small v-on="on">help</v-icon>
                        </template>
                        <span>
                          {{ `app.foods.additional_information_description` | trans }}
                        </span>
                      </v-tooltip>
                    </v-flex>
                    <v-flex xs2>
                      <span>IG: {{ glicemicIndex | round(3) }}</span>
                    </v-flex>
                    <v-flex xs2>
                      <span>ŁG: {{ glicemicLoad | round(3) }}</span>
                    </v-flex>
                    <v-flex xs2>
                      <span>WW: {{ ww | round(3) }}</span>
                    </v-flex>
                    <v-flex xs2>
                      <span>WBT: {{ wbt | round(3) }}</span>
                    </v-flex>
                  </v-layout>
                </div>
              </v-flex>
            </v-layout>
            <v-layout align-center>
              <v-flex xs6 class="align-self-start">
                <v-textarea
                    class="recipe-input"
                    :label="'app.foods.recipe' | trans"
                    v-model.lazy="foodRecipe"
                    :rules="rules.recipe"
                    full
                    solo
                    rows="12"
                    :validate-on-blur="true"
                ></v-textarea>
              </v-flex>
              <v-flex xs6 class="align-self-start">
                <v-autocomplete
                    multiple
                    clearable
                    return-object
                    v-model="food.tags"
                    :items="dictionaries.food_tags.tags"
                    item-text="name"
                    :label="trans('app.tags.tags')"
                    dense
                    :search-input.sync="tagsSearch"
                >
                  <template v-slot:selection="{ item, index }">
                    <v-chip
                        :key="index"
                        class="ma-1"
                        color="green"
                        text-color="white"
                        close
                        @input="removeTag(item)"
                    >
                      {{ item.name }}
                    </v-chip>
                  </template>
                </v-autocomplete>
                <v-autocomplete
                    multiple
                    return-object
                    clearable
                    v-model="food.meals"
                    :items="dictionaries.food_tags.meals"
                    item-text="name"
                    :label="trans('app.foods.meals')"
                    dense
                    :search-input.sync="mealsSearch"
                >
                  <template v-slot:selection="{ item, index }">
                    <v-chip
                        :key="index"
                        class="ma-1"
                        color="green"
                        text-color="white"
                        close
                        @input="removeMeal(item)"
                    >
                      {{ item.name }}
                    </v-chip>
                  </template>
                </v-autocomplete>
                <v-autocomplete
                    multiple
                    return-object
                    clearable
                    v-model="food.diets"
                    :items="dictionaries.food_tags.diets"
                    item-text="name"
                    :label="trans('app.foods.diet')"
                    dense
                    :search-input.sync="dietsSearch"
                >
                  <template v-slot:selection="{ item, index }">
                    <v-chip
                        :key="index"
                        class="ma-1"
                        color="green"
                        text-color="white"
                        close
                        @input="removeDiet(item)"
                    >
                      {{ item.name }}
                    </v-chip>
                  </template>
                </v-autocomplete>
                <v-autocomplete
                    multiple
                    return-object
                    clearable
                    v-model="food.equipments"
                    :items="dictionaries.food_tags.equipments"
                    item-text="name"
                    :label="trans('app.foods.equipment')"
                    dense
                    :search-input.sync="equipmentsSearch"
                >
                  <template v-slot:selection="{ item, index }">
                    <v-chip
                        :key="index"
                        class="ma-1"
                        color="green"
                        text-color="white"
                        close
                        @input="removeEquipment(item)"
                    >
                      {{ item.name }}
                    </v-chip>
                  </template>
                </v-autocomplete>
              </v-flex>
            </v-layout>
          </v-form>
        </v-container>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="primary" flat @click="close">{{ 'app.close' | trans }}</v-btn>
        <v-btn
            color="success"
            @click="save(false)"
            :disabled="buttons.save.disabled || !valid"
            :loading="buttons.save.loading"
        >
          {{ 'app.save' | trans }}
        </v-btn>
        <v-btn
            color="success"
            @click="save(true)"
            :disabled="buttons.saveAndAddAnother.disabled || !valid"
            :loading="buttons.saveAndAddAnother.loading"
        >
          {{ 'app.products.create_modal.save_and_add_another' | trans }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script>
import {trans} from "../../app";
import RulesBuilder from "../../utils/rulesBuilder";
import {mapActions, mapGetters, mapState} from "vuex";
import {
  CARBO,
  FATS,
  FIBER,
  GLICEMIC_INDEX,
  KCAL,
  PROTEIN,
  WBT,
  WW
} from "../../services/NutrientDetailsProvider";
import draggable from "vuedraggable";
import uuid from "uuid4";
import {DIETITIAN_FREE, DIETITIAN_TEST} from "../../utils/roles";

export default {
  components: {
    draggable
  },
  data() {
    return {
      dialog: false,
      valid: false,
      foodId: null,
      buttons: {
        save: {
          loading: false,
          disabled: false,
        },
        saveAndAddAnother: {
          loading: false,
          disabled: false,
        },
      },
      rulesBuilder: null,
      food: {
        is_system: false,
        name: '',
        portions: null,
        recipe: '',
        tags: [],
        meals: [],
        diets: [],
        equipments: [],
        products: []
      },
      productSearchSelected: null,
      productsSearchResults: [],
      hasMoreProductsSearchResults: false,
      loadMoreSearchResultsOffset: 10,
      productsSearchLoading: false,
      productsSearchQuery: '',
      mealsSearch: null,
      dietsSearch: null,
      tagsSearch: null,
      equipmentsSearch: null,
      foodRecipe: '',
      productsSearchFocused: false
    }
  },
  computed: {
    ...mapGetters(['hasPermission', 'userRole', 'ownFoodsCount']),
    ...mapState(['dictionaries']),
    modalTitle() {
      if (this.foodId === null) {
        return trans('app.foods.add_food');
      }

      return trans('app.foods.edit_food');
    },
    rules() {
      return {
        name: this.rulesBuilder.addRequiredRule(trans('app.name'))
            .addMaxLengthRule(trans('app.name'), 255)
            .getRules(),
        portions: this.rulesBuilder.addRequiredRule(trans('app.foods.portions_count'))
            .addIsIntegerRule(trans('app.foods.portions_count'))
            .addNumberBiggerThanRule(trans('app.foods.portions_count'), 1)
            .getRules(),
        recipe: this.rulesBuilder.addRequiredRule(trans('app.foods.recipe'))
            .addMaxLengthRule(trans('app.name'), 10000)
            .getRules(),
      }
    },
    headers() {
      return [
        {
          text: '',
        },
        {
          text: trans('app.product'),
        },
        {
          text: 'Ilość',
        },
        {
          text: 'Miara',
        },
        {
          text: 'Kcal',
        },
        {
          text: 'W (g)',
        },
        {
          text: 'B (g)',
        },
        {
          text: 'T (g)',
        },
        {
          text: 'Bł (g)',
        },
      ]
    },
    totalFoodWeight() {
      return this.food.products.reduce((acc, product) => {
        return acc + Number(product.amount) * Number(product.weight.weight);
      }, 0);
    },
    totalFoodKcal() {
      return this.food.products.reduce((acc, product) => {
        return acc + product.kcal;
      }, 0);
    },
    totalFoodCarbo() {
      return this.food.products.reduce((acc, product) => {
        return acc + product.carbo;
      }, 0);
    },
    totalFoodProtein() {
      return this.food.products.reduce((acc, product) => {
        return acc + product.protein;
      }, 0);
    },
    totalFoodFat() {
      return this.food.products.reduce((acc, product) => {
        return acc + product.fat;
      }, 0);
    },
    totalFoodFiber() {
      return this.food.products.reduce((acc, product) => {
        return acc + product.fiber;
      }, 0);
    },
    totalPortionWeight() {
      if (this.food.portions <= 0) {
        return 0;
      }
      return this.totalFoodWeight / this.food.portions;
    },
    totalPortionKcal() {
      if (this.food.portions <= 0) {
        return 0;
      }
      return this.totalFoodKcal / this.food.portions;
    },
    totalPortionCarbo() {
      if (this.food.portions <= 0) {
        return 0;
      }
      return this.totalFoodCarbo / this.food.portions;
    },
    totalPortionProtein() {
      if (this.food.portions <= 0) {
        return 0;
      }
      return this.totalFoodProtein / this.food.portions;
    },
    totalPortionFat() {
      if (this.food.portions <= 0) {
        return 0;
      }
      return this.totalFoodFat / this.food.portions;
    },
    totalPortionFiber() {
      if (this.food.portions <= 0) {
        return 0;
      }
      return this.totalFoodFiber / this.food.portions;
    },
    glicemicLoad() {
      if (this.food.portions <= 0) {
        return 0;
      }
      return ((this.totalFoodCarbo - this.totalFoodFiber) * this.glicemicIndex) / 100 / this.food.portions;
    },
    glicemicIndex() {
      let totalGlicemicIndex = 0;
      const totalDigestibleCarbohydrates = this.totalFoodCarbo - this.totalFoodFiber
      if (totalDigestibleCarbohydrates === 0) {
        return 0;
      }
      this.food.products.forEach((product) => {
        const productGlicemicIndex = product.glicemicIndex;
        const percentage = (product.carbo - product.fiber) / (totalDigestibleCarbohydrates);

        totalGlicemicIndex += percentage * productGlicemicIndex;
      });
      return totalGlicemicIndex;
    },
    wbt() {
      let totalWBT = 0;
      if (this.food.portions <= 0) {
        return totalWBT;
      }
      this.food.products.forEach((product) => {
        const portionFactor = product.amount * (product.weight.weight / 100)
        const productWBT = product.nutrients.find(nutrient => nutrient.id === WBT);
        totalWBT += productWBT ? productWBT.value * portionFactor : 0;
      });
      return totalWBT / this.food.portions;
    },
    ww() {
      let totalWW = 0;
      if (this.food.portions <= 0) {
        return totalWW;
      }
      this.food.products.forEach((product) => {
        const portionFactor = product.amount * (product.weight.weight / 100)
        const productWW = product.nutrients.find(nutrient => nutrient.id === WW);
        totalWW += productWW ? productWW.value * portionFactor : 0;
      });
      return totalWW / this.food.portions;
    },
  },
  created() {
    this.rulesBuilder = new RulesBuilder();
  },
  methods: {
    ...mapActions(['fetchOwnFoodsCount']),
    KCAL() {
      return KCAL
    },
    CARBO() {
      return CARBO
    },
    FATS() {
      return FATS
    },
    PROTEIN() {
      return PROTEIN
    },
    uuid,
    async open(foodId = null) {
      this.reset();
      if (foodId !== null) {
        this.foodId = foodId;
        const {data} = await axios.get('/v2/foods/' + this.foodId);
        const food = data.data;
        this.food.is_system = food.is_system;
        this.food.name = food.name;
        this.food.portions = food.portions;
        this.food.tags = food.tags;
        this.food.meals = food.meals;
        this.food.diets = food.diets;
        this.food.equipments = food.equipments;
        this.food.products = food.products.map((product) => {
          if (!product._rowId) {
            product._rowId = uuid();
          }

          if (product.weight.id !== 18) {
            product.weight.name = product.weight.name + ' (' + product.weight.weight + ' g)';
          }
          return this.recalculateProductNutrients(product)
        })
        this.foodRecipe = food.recipe;
      }
      this.dialog = true;
    },
    close() {
      this.dialog = false;
      this.reset();
    },
    reset() {
      this.$refs.form.reset();
      this.foodId = null;
      this.food.name = '';
      this.foodRecipe = '';
      this.food.portions = null;
      this.food.is_system = false;
      this.food.tags = [];
      this.food.meals = [];
      this.food.diets = [];
      this.food.equipments = [];
      this.food.products = [];
      this.buttons.save.loading = false;
      this.buttons.save.disabled = false;
      this.buttons.saveAndAddAnother.loading = false;
      this.buttons.saveAndAddAnother.disabled = false;
      this.$nextTick(() => {
        this.$refs.form.resetValidation();
      })
    },
    async save(saveAndAddAnother) {
      if (this.$refs.form.validate() === false) {
        return;
      }

      if (this.food.products.length === 0) {
        this.$toast.error(trans('app.foods.at_least_one_product'));
        return;
      }

      if (saveAndAddAnother) {
        this.buttons.saveAndAddAnother.loading = true;
        this.buttons.save.disabled = true;
      } else {
        this.buttons.save.loading = true;
        this.buttons.saveAndAddAnother.disabled = true;
      }

      const food = {
        name: this.food.name,
        portions: this.food.portions,
        recipe: this.foodRecipe,
        tags: this.food.tags.map((tag) => {
          return tag.id
        }),
        meals: this.food.meals.map((meal) => {
          return meal.id
        }),
        diets: this.food.diets.map((diet) => {
          return diet.id
        }),
        equipments: this.food.equipments.map((equipment) => {
          return equipment.id
        }),
        products: this.food.products.map((product) => {
          return {
            products_id: product.id,
            amount: product.amount,
            weights_id: product.weight.id,
          }
        }),
        is_own: !this.food.is_system
      }

      try {
        if (this.foodId) {
          await axios.patch('/v2/foods/' + this.foodId, food);
        } else {
          await axios.post('/v2/foods', food);
        }
        await this.fetchOwnFoodsCount();
        this.$toast.success(trans('app.saved'));
      } catch (e) {
        this.buttons.save.loading = false;
        this.buttons.saveAndAddAnother.loading = false;
        this.buttons.save.disabled = false;
        this.buttons.saveAndAddAnother.disabled = false;
        if (e.response && e.response.status === 409) {
          if (e.response.data.food_ids.length === 1) {
            this.$confirmWarning(trans('app.food_already_exists_in_database_update')).then(() => {
              this.foodId = e.response.data.food_ids[0];
              this.save(saveAndAddAnother);
            }).catch((error) => {
              this.sentryCaptureException(error);
            })
          } else {
            this.$toast(trans('app.food_already_exists_in_database'));
          }
        } else {
          this.$toast.error(trans('app.save_failed'));
          this.sentryCaptureException(e);
        }
        return;
      }

      this.$emit('food_stored');
      if (saveAndAddAnother) {
        if ((this.userRole === DIETITIAN_FREE || this.userRole === DIETITIAN_TEST) && this.ownFoodsCount >= 20) {
          this.close()
        } else {
          this.reset();
        }
      } else {
        this.close();
      }
      this.buttons.save.loading = false;
      this.buttons.saveAndAddAnother.loading = false;
    },
    getProductWeights(product) {
      return product.weights.map((weight) => {
        let name = weight.name;
        if (weight.id !== 18) {
          name = weight.name + ' (' + weight.weight + ' g)';
        }
        return {
          id: weight.id,
          name: name,
          weight: weight.weight
        }
      })
    },
    onpProductSearchSelected(selectedProduct) {
      if (selectedProduct === null || selectedProduct === undefined) {
        return;
      }
      const weights = selectedProduct.weights;
      const weight = weights.find(weight => weight.id === 18);

      let product = {
        _rowId: uuid(),
        id: selectedProduct.id,
        name: selectedProduct.name,
        amount: 100,
        weights: weights,
        weight: weight,
        nutrients: selectedProduct.nutrients,
      }

      product = this.recalculateProductNutrients(product);

      this.food.products.push(product);

      this.productSearchSelected = null;
      this.productsSearchResults = [];
      this.hasMoreProductsSearchResults = false;
      this.loadMoreSearchResultsOffset = 10;
      this.productsSearchQuery = '';
      this.productsSearchFocused = false;
    },
    productAmountInput(product, newAmount) {
      const n = Number(newAmount);
      if (newAmount === '' || Number.isNaN(n)) {
        return;
      }
      product.amount = n;
      this.recalculateProductNutrients(product)
    },
    productAmountCommit(product, newAmount) {
      const n = Number(newAmount);
      if (newAmount === '' || Number.isNaN(n) || n < 0.01) {
        product.amount = 0.01;
      } else {
        product.amount = n;
      }
      this.recalculateProductNutrients(product)
    },
    recalculateProductNutrients(product) {
      if (product.weight === undefined) {
        return product;
      }

      const portionFactor = product.amount * (product.weight.weight / 100)
      const kcal = product.nutrients.find(nutrient => nutrient.id === KCAL);
      product.kcal = kcal ? kcal.value * portionFactor : 0;
      const carbo = product.nutrients.find(nutrient => nutrient.id === CARBO);
      product.carbo = carbo ? carbo.value * portionFactor : 0;
      const protein = product.nutrients.find(nutrient => nutrient.id === PROTEIN);
      product.protein = protein ? protein.value * portionFactor : 0;
      const fats = product.nutrients.find(nutrient => nutrient.id === FATS);
      product.fat = fats ? fats.value * portionFactor : 0;
      const fiber = product.nutrients.find(nutrient => nutrient.id === FIBER);
      product.fiber = fiber ? fiber.value * portionFactor : 0;
      const glicemicIndex = product.nutrients.find(nutrient => nutrient.id === GLICEMIC_INDEX);
      product.glicemicIndex = glicemicIndex ? glicemicIndex.value : 0;
      return product;
    },
    removeProduct(index) {
      this.food.products.splice(index, 1);
    },
    removeTag(tag) {
      this.food.tags = this.food.tags.filter((foodTag) => {
        return foodTag.id !== tag.id;
      });
    },
    removeMeal(meal) {
      this.food.meals = this.food.meals.filter((foodMeal) => {
        return foodMeal.id !== meal.id;
      });
    },
    removeDiet(diet) {
      this.food.diets = this.food.diets.filter((foodDiet) => {
        return foodDiet.id !== diet.id;
      });
    },
    removeEquipment(equipment) {
      this.food.equipments = this.food.equipments.filter((foodEquipment) => {
        return foodEquipment.id !== equipment.id;
      });
    },
    productHasNutrient(product, nutrientId) {
      return product.nutrients.some(nutrient => nutrient.id === nutrientId);
    },
    async loadMoreSearchResults() {
      this.productsSearchFocused = true;
      const response = await axios.get('/products/all-with-weights.json?q=' + this.productsSearchQuery + '&offset=' + this.loadMoreSearchResultsOffset + '&limit=10');
      response.data.items.forEach(item => {
        this.productsSearchResults.push(item);
      });
      this.hasMoreProductsSearchResults = this.productsSearchResults.length < response.data.total_count;
      if (this.hasMoreProductsSearchResults) {
        this.loadMoreSearchResultsOffset += response.data.items.length;
      }
    },
  },
  watch: {
    productsSearchQuery: async function (query) {
      this.loadMoreSearchResultsOffset = 10;
      if (!query || query.length < 3) {
        this.productsSearchResults = [];
        this.hasMoreProductsSearchResults = false;
        return;
      }

      this.productsSearchLoading = true;
      const response = await axios.get('/products/all-with-weights.json?q=' + query);
      this.productsSearchResults = response.data.items;
      this.hasMoreProductsSearchResults = this.productsSearchResults.length < response.data.total_count;
      this.productsSearchLoading = false;
    },
    'food.tags': function () {
      this.tagsSearch = null;
    },
    'food.meals': function () {
      this.mealsSearch = null;
    },
    'food.diets': function () {
      this.dietsSearch = null;
    },
    'food.equipments': function () {
      this.equipmentsSearch = null;
    },
  }
}
</script>
<style scoped>
.header-cell {
  font-weight: bold;
  color: #000000de;
}

.drag-handle:hover {
  cursor: grab;
}

.additional-info-container {
  font-size: 13px;
  padding-top: 40px;
}

>>> table.v-table thead tr {
  height: 30px;
}

>>> table.v-table tbody td {
  height: 30px;
}

>>> table.v-table tfoot tr {
  height: 30px;
  font-size: 13px;
}

>>> table.v-table thead th:not(:first-child) {
  padding: 0 12px;
}

table.v-table thead th:first-child {
  padding: 0 12px;
}

>>> table.v-table tbody td:first-child {
  padding: 0 12px;
}

>>> table.v-table tbody td:not(:first-child) {
  padding: 0 12px;
}

>>> table.v-table tfoot td:not(:first-child) {
  padding: 0 12px;
}

>>> table.v-table tfoot tr:first-child {
  border-top: none;
}

>>> table.v-table tfoot tr:nth-child(2) {
  border-top: none;
}

>>> .v-chip .v-chip__content {
  height: 25px;
}

>>> .container {
  padding: 0;
}

>>> .v-card__text {
  padding-top: 0;
}

.system-food-checkbox-container {
  height: 50px;
}

.system-food-checkbox {
  margin-top: 0;
  padding-top: 0;
}

.product-amount-input {
  font-size: 13px;
  padding-top: 0;
  margin-top: 0;
}

.weight-select {
  font-size: 13px;
  padding-top: 0;
  margin-top: 0;
}

.recipe-input {
  font-size: 13px;
  margin-top: 10px;
}

.product-search-input {
  padding-top: 10px;
  padding-bottom: 10px;
}

.total-n-portions {
  min-width: 160px;
}

.product-search-nutrients {
  display: flex;
  position: absolute;
  right: 20px;
  width: 250px;
  justify-content: space-between;
  align-items: center;
}

>>> .icheckbox_square-green {
  display: none;
}
</style>
